import React from 'react';

const GOLD = '#C9A84C';

function stripHtmlToMarkdown(text) {
  if (!text) return text;
  let s = String(text);
  s = s.replace(/<em>([^<]*)<\/em>/gi, '*$1*');
  s = s.replace(/<i>([^<]*)<\/i>/gi, '*$1*');
  s = s.replace(/<strong>([^<]*)<\/strong>/gi, '**$1**');
  s = s.replace(/<b>([^<]*)<\/b>/gi, '**$1**');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/p>\s*<p[^>]*>/gi, '\n\n');
  s = s.replace(/<\/?p[^>]*>/gi, '\n\n');
  s = s.replace(/<[^>]+>/g, '');
  s = s.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&nbsp;/g, ' ');
  s = s.replace(/&copy;?/gi, '©');
  s = s.replace(/©[^\n]*/g, '');
  return s;
}

function renderInlineFormatting(text, keyPrefix = 'inline', onLinkClick = null, linkNames = null) {
  if (!text) return null;

  // Build a regex that also splits on known linkable names (case-insensitive, whole word)
  let splitPattern = /(\*\*[^*]+\*\*|\*[^*]+\*|\[\[[^\]]+\]\])/g;
  const nameSet = new Set();
  if (linkNames && linkNames.length > 0 && onLinkClick) {
    const escaped = linkNames
      .filter(n => n && n.length >= 2)
      .map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (escaped.length > 0) {
      escaped.forEach(n => nameSet.add(n.toLowerCase().replace(/\\(.)/g, '$1')));
      const nameAlts = escaped.join('|');
      try {
        splitPattern = new RegExp(`(\\*\\*[^*]+\\*\\*|\\*[^*]+\\*|\\[\\[[^\\]]+\\]\\]|\\b(?:${nameAlts})\\b)`, 'gi');
      } catch {
        // Safari may reject certain patterns — fall back to plain markup-only split
      }
    }
  }

  const parts = String(text).split(splitPattern);
  return parts.map((part, j) => {
    if (!part) return null;
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) {
      return <strong key={`${keyPrefix}-${j}`} className="font-semibold" style={{ color: 'var(--text-primary, #fff)' }}>{boldMatch[1]}</strong>;
    }
    const italicMatch = part.match(/^\*([^*]+)\*$/);
    if (italicMatch) {
      return <em key={`${keyPrefix}-${j}`} className="italic">{italicMatch[1]}</em>;
    }
    const linkMatch = part.match(/^\[\[([^\]]+)\]\]$/);
    if (linkMatch) {
      const name = linkMatch[1];
      if (onLinkClick) {
        return (
          <button
            key={`${keyPrefix}-${j}`}
            onClick={(e) => { e.stopPropagation(); onLinkClick(name, e); }}
            className="font-medium hover:underline transition-colors cursor-pointer"
            style={{ color: GOLD }}
          >
            {name}
          </button>
        );
      }
      return <span key={`${keyPrefix}-${j}`} className="font-medium" style={{ color: GOLD }}>{name}</span>;
    }
    // Auto-link known names
    if (nameSet.size > 0 && nameSet.has(part.toLowerCase()) && onLinkClick) {
      return (
        <button
          key={`${keyPrefix}-${j}`}
          onClick={(e) => { e.stopPropagation(); onLinkClick(part, e); }}
          className="font-medium hover:underline transition-colors cursor-pointer"
          style={{ color: GOLD }}
        >
          {part}
        </button>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${j}`}>{part}</React.Fragment>;
  });
}

/**
 * Pre-processes text that arrives without markdown paragraph breaks.
 * Detects "Quick Facts" style dash-separated key-value lists and
 * long prose without paragraph breaks, converting them to properly
 * separated paragraphs.
 */
/**
 * Inserts paragraph breaks before section headers that were glued onto the prior
 * line by enrichment or legacy cache (e.g. "Real Name: … Notable Albums").
 */
function repairEmbeddedBioSectionHeaders(text) {
  if (!text) return text;
  const headers = [
    'Quick Facts',
    'Notable Albums',
    'Key Works',
    'At a Glance',
    'Legacy',
    'The Beginning',
    'Rise to Fame',
    'Creative Peak',
    'Key Facts',
    'Key Details',
    'Selected Credits',
    'Early Life',
  ];
  let s = String(text);
  for (const header of headers) {
    const escaped = header.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    s = s.replace(new RegExp(`([^\\n])(#{1,3}\\s+${escaped}\\b)`, 'gi'), '$1\n\n$2');
    s = s.replace(
      new RegExp(`(:\\s*[^\\n#]{1,200}?)\\s+(?:#{1,3}\\s+)?(${escaped})\\b(?!\\s*[:-])`, 'gi'),
      '$1\n\n## $2',
    );
  }
  return s;
}

function ensureParagraphs(text) {
  if (!text) return text;

  const existingParas = text.split(/\n{2,}/).filter(p => p.trim());
  if (existingParas.length > 1) {
    const hasLongBlob = existingParas.some(p => p.trim().length > 500);
    if (!hasLongBlob) return text;
    return existingParas.map(p => {
      const t = p.trim();
      return t.length > 500 ? splitProse(t) : p;
    }).join('\n\n');
  }

  if (text.length < 300) return text;

  // Detect "Quick Facts" or "At a Glance" style: "Header - Key: Value - Key: Value - ..."
  const qfPattern = /^(.+?(?:Quick Facts|At a Glance|Key Facts|Key Details))\s*-\s*/i;
  const qfMatch = text.match(qfPattern);
  if (qfMatch) {
    const headerText = qfMatch[1].trim();
    const remainder = text.slice(qfMatch[0].length);
    const segments = remainder.split(/\s+-\s+/);

    const facts = [];
    let proseStartIdx = -1;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i].trim();
      if (seg.includes(':') && seg.length < 200) {
        facts.push(seg);
      } else {
        proseStartIdx = i;
        break;
      }
    }

    if (facts.length >= 3) {
      let result = `## ${headerText}\n\n`;
      result += facts.map(f => `- ${f}`).join('\n');
      if (proseStartIdx >= 0) {
        const prose = segments.slice(proseStartIdx).join(' - ').trim();
        result += '\n\n' + splitProse(prose);
      }
      return result;
    }
  }

  return splitProse(text);
}

function splitProse(text) {
  if (!text || text.length < 300) return text;
  // Safari-safe sentence split — no lookbehind. Inserts a sentinel after
  // sentence-ending punctuation + whitespace, then splits on the sentinel.
  const sentences = text.replace(/([.!?])\s+/g, '$1\x00').split('\x00').filter(Boolean);
  if (sentences.length <= 3) return text;

  // Detect natural topic shifts by looking for proper-noun starts after periods
  const paragraphs = [];
  let current = [];
  for (let i = 0; i < sentences.length; i++) {
    current.push(sentences[i]);
    const joined = current.join(' ');
    // Break every ~3-4 sentences, or when a sentence starts a clearly new topic
    if (current.length >= 3 && joined.length > 250) {
      paragraphs.push(current.join(' '));
      current = [];
    }
  }
  if (current.length > 0) {
    paragraphs.push(current.join(' '));
  }
  return paragraphs.join('\n\n');
}

/**
 * Strips list items whose value is "Unknown" and removes section headers
 * that become empty as a result. Catches cached bios with placeholder values.
 */
function stripUnknownValues(text) {
  if (!text) return text;

  const lines = text.split('\n');
  const filtered = [];

  for (const line of lines) {
    const trimmed = line.trim();

    if (/^[-•]\s/.test(trimmed)) {
      const content = trimmed.replace(/^[-•]\s*/, '').trim();

      if (/^unknown$/i.test(content)) continue;

      const boldLabel = content.match(/^\*\*[^*]+\*\*:?\s*(.*)/);
      if (boldLabel && /^unknown$/i.test(boldLabel[1].trim())) continue;

      const plainLabel = content.match(/^[^:]+:\s*(.*)/);
      if (plainLabel && /^unknown$/i.test(plainLabel[1].trim())) continue;
    }

    filtered.push(line);
  }

  const result = [];
  for (let i = 0; i < filtered.length; i++) {
    const trimmed = filtered[i].trim();

    if (/^#{1,4}\s/.test(trimmed)) {
      let hasContent = false;
      for (let j = i + 1; j < filtered.length; j++) {
        const next = filtered[j].trim();
        if (!next) continue;
        if (/^#{1,4}\s/.test(next)) break;
        hasContent = true;
        break;
      }
      if (!hasContent) continue;
    }

    result.push(filtered[i]);
  }

  return result.join('\n');
}

/**
 * Shared rich-text renderer for markdown-structured content from the enrichment pipeline.
 * Handles: # H1, ## H2, ### H3, #### H4, --- dividers, - lists, **bold**, *italic*, [[links]],
 * > Quick Facts/Notable Albums blocks, and regular paragraphs.
 *
 * Also handles raw unformatted text by auto-detecting and inserting paragraph breaks.
 *
 * Usage: <FormattedContent text={biography} entityName="Miles Davis" />
 */
export default function FormattedContent({ text, entityName, className = '', onLinkClick = null, linkNames = null }) {
  if (!text) return null;

  let sanitized = String(text || '').trim();
  if (!sanitized) return null;

  // Convert any embedded HTML to markdown and strip copyright
  sanitized = stripHtmlToMarkdown(sanitized);
  sanitized = sanitized.trim();
  if (!sanitized) return null;

  // Strip "Name: A Maestro Biography Overview" from start of bio (with or without # prefix)
  sanitized = sanitized.replace(/^(?:#\s+)?[^:\n]{2,80}:\s*A\s+Maestro\s+Biography\s+Overview\s*/i, '');
  // Strip any remaining standalone or inline occurrences
  sanitized = sanitized.replace(/\bA\s+Maestro\s+Biography\s+Overview\b\s*/gi, '');

  // Strip leading "# Entity Name" if it duplicates what the page already shows
  if (entityName) {
    const lines = sanitized.split('\n');
    const firstContentLine = lines.findIndex(l => l.trim().length > 0);
    if (firstContentLine >= 0) {
      const line = lines[firstContentLine].trim();
      if (line.startsWith('# ') && line.toLowerCase().includes(entityName.toLowerCase().slice(0, 10))) {
        lines.splice(firstContentLine, 1);
        sanitized = lines.join('\n').trim();
      }
    }
  }

  sanitized = stripUnknownValues(sanitized);
  sanitized = repairEmbeddedBioSectionHeaders(sanitized);
  sanitized = ensureParagraphs(sanitized);

  const sections = sanitized.split('\n\n').filter(Boolean);
  const fmt = (t, key) => renderInlineFormatting(t, key, onLinkClick, linkNames);

  return (
    <div className={`formatted-content ${className}`}>
      {sections.map((section, i) => {
        const trimmed = section.trim();
        if (!trimmed) return null;

        if (trimmed === '---') {
          return <hr key={i} style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }} className="my-6" />;
        }

        // H1
        if (trimmed.startsWith('# ') && !trimmed.startsWith('## ')) {
          const headerText = trimmed.slice(2).trim();
          return (
            <h2 key={i} className="text-2xl md:text-3xl font-bold mt-6 mb-3" style={{ color: 'var(--text-primary, #fff)' }}>
              {fmt(headerText, `h1-${i}`)}
            </h2>
          );
        }

        // H2
        if (trimmed.startsWith('## ') && !trimmed.startsWith('### ')) {
          const headerAndBody = trimmed.slice(3).trim();
          const lines = headerAndBody.split('\n');
          const headerText = lines[0].trim();
          const bodyText = lines.slice(1).join('\n').trim();

          const isInfoSection = /^(Quick Facts|Notable Albums|Key Works|At a Glance)\b/i.test(headerText);
          return (
            <div key={i}>
              <h3
                className={isInfoSection ? 'font-bold mt-6 mb-2' : 'text-xl font-bold mt-6 mb-2'}
                style={{ fontSize: isInfoSection ? 18 : undefined, fontWeight: 700, color: 'var(--text-primary, #fff)' }}
              >
                {fmt(headerText, `h2-${i}`)}
              </h3>
              {bodyText && <div className="mb-3 leading-relaxed text-sm" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>{renderBlock(bodyText, `h2b-${i}`, onLinkClick, linkNames)}</div>}
            </div>
          );
        }

        // H3
        if (trimmed.startsWith('### ')) {
          const headerAndBody = trimmed.slice(4).trim();
          const lines = headerAndBody.split('\n');
          const headerText = lines[0].trim();
          const bodyText = lines.slice(1).join('\n').trim();
          return (
            <div key={i}>
              <h4 className="text-base font-bold mt-4 mb-1" style={{ color: 'var(--text-primary, #fff)' }}>
                {fmt(headerText, `h3-${i}`)}
              </h4>
              {bodyText && <div className="mb-3 leading-relaxed text-sm" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>{renderBlock(bodyText, `h3b-${i}`, onLinkClick, linkNames)}</div>}
            </div>
          );
        }

        // H4
        if (trimmed.startsWith('#### ')) {
          const headerText = trimmed.slice(5).trim();
          return (
            <h5 key={i} className="text-sm font-bold mt-3 mb-1" style={{ color: 'var(--text-primary, rgba(255,255,255,0.9))' }}>
              {fmt(headerText, `h4-${i}`)}
            </h5>
          );
        }

        // List block
        if (/^[-•]\s/.test(trimmed)) {
          const items = trimmed.split('\n').filter(l => /^[-•]\s/.test(l.trim()));
          return (
            <ul key={i} className="list-none space-y-1 mb-4 pl-1">
              {items.map((item, j) => (
                <li key={j} className="flex gap-2 text-sm leading-relaxed" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>
                  <span className="mt-0.5" style={{ color: 'rgba(201,168,76,0.6)' }}>-</span>
                  <span>{fmt(item.replace(/^[-•]\s*/, ''), `li-${i}-${j}`)}</span>
                </li>
              ))}
            </ul>
          );
        }

        // Blockquote
        if (trimmed.startsWith('> ')) {
          const quoteText = trimmed.split('\n').map(l => l.replace(/^>\s?/, '')).join(' ');
          return (
            <blockquote key={i} className="border-l-2 pl-4 my-4 italic text-sm leading-relaxed" style={{ borderColor: 'rgba(201,168,76,0.4)', color: 'var(--text-tertiary, rgba(255,255,255,0.6))' }}>
              {fmt(quoteText, `bq-${i}`)}
            </blockquote>
          );
        }

        // Implicit section header: short line, no ending punctuation, starts uppercase.
        // Catches "Legacy", "Selected Credits", "Early Life", etc. that lack ## prefix.
        if (
          !trimmed.includes('\n') &&
          trimmed.length <= 60 &&
          /^[A-Z]/.test(trimmed) &&
          !/[.!?:,;]$/.test(trimmed) &&
          !/^\d/.test(trimmed)
        ) {
          return (
            <h3 key={i} className="text-xl font-bold mt-6 mb-2" style={{ fontWeight: 700, color: 'var(--text-primary, #fff)' }}>
              {fmt(trimmed, `ih-${i}`)}
            </h3>
          );
        }

        // Regular paragraph
        return (
          <p key={i} className="mb-3 leading-relaxed text-sm" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>
            {fmt(trimmed.replace(/\n/g, ' '), `p-${i}`)}
          </p>
        );
      })}
    </div>
  );
}

function renderBlock(text, keyPrefix, onLinkClick = null, linkNames = null) {
  const lines = text.split('\n');
  return lines.map((line, j) => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    if (/^[-•]\s/.test(trimmed)) {
      return (
        <div key={`${keyPrefix}-${j}`} className="flex gap-2 text-sm leading-relaxed" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>
          <span className="mt-0.5" style={{ color: 'rgba(201,168,76,0.6)' }}>-</span>
          <span>{renderInlineFormatting(trimmed.replace(/^[-•]\s*/, ''), `${keyPrefix}-li-${j}`, onLinkClick, linkNames)}</span>
        </div>
      );
    }
    return (
      <p key={`${keyPrefix}-${j}`} className="mb-2 leading-relaxed text-sm" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>
        {renderInlineFormatting(trimmed, `${keyPrefix}-p-${j}`, onLinkClick, linkNames)}
      </p>
    );
  });
}

/**
 * Skeleton loader for content areas. Shows animated placeholders while data loads.
 * Use instead of spinners for text-heavy content.
 */
export function ContentSkeleton({ lines = 6, hasHeader = true, hasImage = false }) {
  return (
    <div className="animate-pulse">
      {hasImage && (
        <div className="w-full aspect-[2/1] rounded-xl mb-6" style={{ background: 'var(--bg-secondary, rgba(255,255,255,0.05))' }} />
      )}
      {hasHeader && (
        <div className="h-8 rounded-lg w-2/3 mb-4" style={{ background: 'var(--bg-secondary, rgba(255,255,255,0.08))' }} />
      )}
      <div className="space-y-3">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className="h-4 rounded"
            style={{ width: `${Math.max(40, 100 - i * 8 - Math.random() * 15)}%`, background: 'var(--bg-secondary, rgba(255,255,255,0.05))' }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for a grid of cards (albums, artists, etc.)
 */
export function CardGridSkeleton({ count = 8, columns = 4 }) {
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-${columns} gap-4 animate-pulse`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>
          <div className="aspect-square rounded-lg mb-2" style={{ background: 'var(--bg-secondary, rgba(255,255,255,0.05))' }} />
          <div className="h-3 rounded w-3/4 mb-1" style={{ background: 'var(--bg-secondary, rgba(255,255,255,0.05))' }} />
          <div className="h-3 rounded w-1/2" style={{ background: 'var(--bg-secondary, rgba(255,255,255,0.05))' }} />
        </div>
      ))}
    </div>
  );
}
